import * as React from 'react';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import Retracked from 'js/app/retracked';
import useRouter from 'js/lib/useRouter';

import { handleRedirect } from 'bundles/enterprise-learner-onboarding/utils';
import type { Product, ProductType } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { handleProgramRedirect } from 'bundles/enterprise-legacy-learner-home/utils/handleProgramRedirect';
import ProgramMiniModal, {
  closeMiniModalWithRoute,
  useRouterParams,
} from 'bundles/enterprise-legacy-xdp/components/ProgramMiniModal';
import { getProductPath, launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import { IdTypes } from 'bundles/product-features';
import EnterpriseBanner from 'bundles/program-home/components/enterprise-home/EnterpriseBanner';
import type { PropsFromEnterpriseHomeApp } from 'bundles/program-home/components/enterprise-home/EnterpriseHomeApp';
import EnterpriseHomeTabs from 'bundles/program-home/components/enterprise-home/EnterpriseHomeTabs';
import { EnterpriseHomeHorizontalDomainsMenu } from 'bundles/program-home/components/multiprogram/HorizontalDomainsMenu';
import { useProductFeatures } from 'bundles/program-home/productFeatureFlags';

import 'css!./__styles__/EnterpriseHomePage';

import { transformDomainsToLegacy, transformSubdomainsToLegacy } from './utils/typeTransformations';

type Props = PropsFromEnterpriseHomeApp;

export function EnterpriseHomePage({
  thirdPartyOrganization,
  userId,
  enterprisePrograms,
  programInvitations,
  userEmails,
  isBrowseOnlyProgram,
  allowOrgForCurriculumBuilder,
  allowOrgForSpecializationConfiguration,
  shouldShowSponsoredByMessage,
  domains,
  subdomains,
  enableSkillsDashboard,
  showSkillSetRoleFilter,
}: Props) {
  const router = useRouter();
  const onProductCardClick = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (product: Product, productType: ProductType, collectionId?: string | null) => {
      launchMiniModal(router, product.id, productType === 'course', collectionId || '', undefined, undefined, {
        slug: product.slug,
        productPagePath: getProductPath(product),
      });
    },
    [router]
  );
  const onCloseMiniModal = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => closeMiniModalWithRoute(router),
    [router]
  );

  const { features, loading: isFeaturesLoading } = useProductFeatures({
    idType: IdTypes.Organization,
    id: thirdPartyOrganization?.id || '',
    skip: !thirdPartyOrganization?.id,
    ssr: true,
  });

  // TODO(ppaskaris): loading state
  if (!thirdPartyOrganization || !userId) {
    return null;
  }

  // if feature not loaded, don't show anything
  if (isFeaturesLoading) {
    return null;
  }

  // redirect to `/programs/landing/orgSlug` if `enableOrgHomepage` is true
  if (features.get('enterprise', 'enableOrgHomepage')) {
    const redirectPath =
      handleProgramRedirect(userId, enterprisePrograms, programInvitations) ??
      '/programs/landing/' + thirdPartyOrganization.slug;
    handleRedirect(redirectPath, router);

    return null;
  }

  const isMultiProgram = enterprisePrograms.length > 1;

  // Not a react hook, just a poorly aged identifier.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const routerParams = useRouterParams(router);

  // Transform fragment types to legacy types using utility functions
  const transformedDomains = domains ? transformDomainsToLegacy(domains) : [];
  const transformedSubdomains = subdomains ? transformSubdomainsToLegacy(subdomains) : [];

  return (
    <div className="rc-EnterpriseHomePage">
      <EnterpriseHomeHorizontalDomainsMenu domains={transformedDomains} subdomains={transformedSubdomains} />
      <div role="main">
        <EnterpriseBanner
          thirdPartyOrganization={thirdPartyOrganization}
          isMultiProgram={isMultiProgram}
          isProgramDetailsView={false}
          shouldShowJoinButton={false}
          isUpswell={false}
          isC4cv={false}
        />
        <EnterpriseHomeTabs
          isMultiProgram={isMultiProgram}
          enterprisePrograms={enterprisePrograms}
          programInvitations={programInvitations}
          userId={userId}
          onProductCardClick={onProductCardClick}
          thirdPartyOrganization={thirdPartyOrganization}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          thirdPartyOrgSlug={thirdPartyOrganization.slug}
          userEmails={userEmails}
          allowOrgForCurriculumBuilder={allowOrgForCurriculumBuilder}
          allowOrgForSpecializationConfiguration={allowOrgForSpecializationConfiguration}
          enableSkillsDashboard={enableSkillsDashboard}
          showSkillSetRoleFilter={showSkillSetRoleFilter}
        />
      </div>
      {routerParams && (
        <ProgramMiniModal
          thirdPartyOrganizationName={thirdPartyOrganization.name}
          renderCourseHomeLink
          onCloseMiniModal={onCloseMiniModal}
          isUpswell={false}
          hideAction={isBrowseOnlyProgram}
          disableSponsoredByMessage={!shouldShowSponsoredByMessage}
          thirdPartyOrgId={thirdPartyOrganization.id}
          spookyMultiprogramFlag
          {...routerParams}
        />
      )}
    </div>
  );
}

export default compose<Props, Props>(
  setDisplayName(getDisplayName(EnterpriseHomePage)),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_home_v3',
      page: 'enterprise_home_page',
    },
  }))
)(EnterpriseHomePage);
